import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OrganizationEvents } from "@app/shared/src/api/api.gen";
import { FacilityEvent } from "@app/shared/src/models";
import { usePoll } from "./usePoll";

/**
 * Every event in a trailing window, for client-side aggregation (charts, KPIs, breakdowns).
 *
 * There is no per-day/per-type aggregate endpoint, so the overview pages the raw events and
 * buckets locally. Capped so a traffic-heavy site can't pull the console under: past the cap the
 * result is flagged `truncated` and every chart that uses it says so rather than presenting a
 * partial window as the whole story.
 *
 * `deviceIds` narrows the result to a set of units. The query itself takes one device or one
 * facility and never a set, so the narrowing happens here, on the window that was already
 * fetched — which also means one fetch serves any scope the caller changes to.
 */

const PAGE = 500;
const MAX_PAGES = 4;
const REFRESH_MS = 60_000;

export type OrgEventsState = {
    events: FacilityEvent[];
    isLoading: boolean;
    error?: string;
    /** True when the window held more than MAX_PAGES × PAGE events — charts show newest-first data. */
    truncated: boolean;
    reload: () => void;
};

export function useOrgEvents(orgId: string | undefined, days: number, deviceIds?: string[]): OrgEventsState {
    const [all, setAll] = useState<FacilityEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [truncated, setTruncated] = useState(false);
    const seq = useRef(0);

    const load = useCallback(
        async (quiet: boolean) => {
            if (!orgId) return;
            const mySeq = ++seq.current;
            if (!quiet) setIsLoading(true);
            try {
                const dateFrom = new Date(Date.now() - days * 86_400_000);
                const events: FacilityEvent[] = [];
                let cursor: string | undefined;
                let more = false;
                for (let page = 0; page < MAX_PAGES; page++) {
                    const result = await OrganizationEvents.getByOrganization(
                        orgId, PAGE, "dateCreated", "desc", undefined, undefined, undefined, undefined, undefined, dateFrom, undefined, cursor,
                    );
                    events.push(...(result?.data ?? []));
                    more = Boolean(result?.hasMore);
                    cursor = result?.nextCursor;
                    if (!more || !cursor) break;
                }
                if (seq.current !== mySeq) return;
                setAll(events);
                setTruncated(more);
                setError(undefined);
            } catch (err) {
                if (seq.current === mySeq) setError(err instanceof Error ? err.message : "Could not load events");
            } finally {
                if (seq.current === mySeq) setIsLoading(false);
            }
        },
        [orgId, days],
    );

    const reload = useCallback(() => load(false), [load]);

    useEffect(() => {
        load(false);
    }, [load]);

    usePoll(() => load(true), REFRESH_MS);

    const scopeKey = deviceIds?.join(",");
    const events = useMemo(() => {
        if (scopeKey === undefined) return all;
        const scope = new Set(scopeKey ? scopeKey.split(",") : []);
        return all.filter((e) => e.deviceId && scope.has(e.deviceId));
    }, [all, scopeKey]);

    return { events, isLoading, error, truncated, reload };
}
